import {useCallback, useEffect, useRef, useState} from 'react'

import {useSuiContext} from '@s-ui/react-context'

// Ported verbatim from legacy/DetailFormContactSellerModalWrapper.js, and
// scheduled only AFTER eligibility resolves, like legacy does.
const OPEN_MODAL_DELAY_TIME = 1000

const POST_CONTACT_MODALS = {LAST_CALL: 'LAST_CALL', RELATED_ADS: 'RELATED_ADS'}

/**
 * Legacy opens exactly one modal after a message lead — Last Call when the ad is
 * eligible, the related-ads modal otherwise (`showLastCall ? ... : ...` in
 * DetailFormContactSellerModalWrapper.js:143). This hook owns that either/or, so
 * the two can never overlap and the fallback can never be forgotten.
 *
 * The motos exclusion and the VN/KM0 filter are hardcoded in
 * `show_last_call_use_case` and short-circuit with no network call, so there is
 * deliberately no tenant or offer-type branch here.
 */
const usePostContactModal = ({adId, dealerId, hasRelatedAds = false, makeId, offerTypeId} = {}) => {
  const {domain} = useSuiContext()

  const [openModal, setOpenModal] = useState(null)
  const [contactType, setContactType] = useState(null)
  const [contactUserData, setContactUserData] = useState(null)
  const hasTriggeredRef = useRef(false)
  const openTimerRef = useRef(undefined)

  useEffect(() => () => clearTimeout(openTimerRef.current), [])

  // The lead response is the primary source of the buyer id, but it does not
  // always carry one, so fall back to the session like legacy does
  // (DetailFormContactSellerModalWrapper.js:53-67). Without this, an already
  // logged-in visitor whose lead answered without a userId silently loses Last
  // Call. `get_current_user_use_case` is not inlineError-decorated and rejects
  // rather than returning a tuple, hence the catch.
  const resolveUserId = useCallback(
    async userId => {
      if (userId) {
        return userId
      }

      const currentUser = await domain
        .get('get_current_user_use_case')
        .execute()
        .catch(() => null)

      return currentUser?.userId
    },
    [domain]
  )

  const isLastCallEligible = useCallback(
    async userId => {
      // Fails closed onto the related-ads fallback. No `userId` means neither the
      // lead nor the session identified the buyer, and the eligibility request
      // value object throws on any empty param; no `dealerId` means there is no
      // dealer to route a Last Call to.
      if (!userId || !dealerId) {
        return false
      }

      const [error, response] = await domain.get('show_last_call_use_case').execute({adId, makeId, userId, offerTypeId})

      return !error && Boolean(response?.show)
    },
    [adId, dealerId, domain, makeId, offerTypeId]
  )

  const triggerPostContactModal = useCallback(
    async ({contactType: leadContactType, email, name, phone, userId: leadUserId, zipCode} = {}) => {
      // Guards the await window below, not the whole page life: eligibility is
      // async and a double submit would otherwise schedule two timers. Released
      // when the modal closes, so a second lead in the same visit gets its own
      // modal — legacy's wrapper remounts per lead and its useMount fires again.
      if (hasTriggeredRef.current) {
        return
      }

      hasTriggeredRef.current = true

      let userId
      let isEligible

      try {
        userId = await resolveUserId(leadUserId)
        isEligible = await isLastCallEligible(userId)
      } catch (error) {
        hasTriggeredRef.current = false

        return
      }

      const nextModal = isEligible ? POST_CONTACT_MODALS.LAST_CALL : POST_CONTACT_MODALS.RELATED_ADS

      // The related-ads modal renders nothing without ads, and its own tracking
      // effect would still not fire, but an empty mount is pointless noise.
      // Releases the guard: nothing opened, so there is no close to release it.
      if (nextModal === POST_CONTACT_MODALS.RELATED_ADS && !hasRelatedAds) {
        hasTriggeredRef.current = false

        return
      }

      setContactType(leadContactType)
      setContactUserData({email, name, phone, userId, zipCode})
      openTimerRef.current = setTimeout(() => setOpenModal(nextModal), OPEN_MODAL_DELAY_TIME)
    },
    [hasRelatedAds, isLastCallEligible, resolveUserId]
  )

  const onClosePostContactModal = useCallback(() => {
    hasTriggeredRef.current = false
    setOpenModal(null)
  }, [])

  return {contactType, contactUserData, onClosePostContactModal, openModal, triggerPostContactModal}
}

export {POST_CONTACT_MODALS}

export default usePostContactModal
